import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { catchError, map, of, switchMap, timer } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CompanyClient } from 'src/app/trip-calculator/models/companyClients';
import { LogoUploadComponent } from 'src/app/shared/components/logo-upload/logo-upload.component';
import { CompanyClientService } from 'src/app/shared/services/company-client.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-client-form-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    DialogModule,
    TranslocoModule,
    LogoUploadComponent,
  ],
  templateUrl: './client-form-dialog.component.html',
})
export class ClientFormDialogComponent implements OnChanges {
  /** Build-time switch, mirrors FEATURE_*_ENABLED on the API. */
  protected readonly features = environment.features;
  @Input() visible = false;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() client?: CompanyClient;
  @Input() loading = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<{
    name: string;
    publicId: string;
    logo?: Blob;
  }>();

  private fb = inject(FormBuilder);
  private companyClientService = inject(CompanyClientService);
  pendingLogo?: Blob;

  /**
   * Checks the Greentripper Public ID against the server as the user types,
   * instead of only on Save — so a mistake surfaces while they're still
   * editing, not after, where it would otherwise cost them their other
   * changes (the dialog stays open with nothing saved on a rejected submit).
   * Debounced via timer() + switchMap: every keystroke cancels the previous
   * pending wait/request, so only one request fires per pause in typing.
   * An empty value is valid immediately, with no round trip — it just means
   * "no reference set" (and is how an existing value gets cleared).
   */
  private publicIdValidator: AsyncValidatorFn = (control: AbstractControl) => {
    const value = (control.value as string | null)?.trim();
    if (!value) return of(null);

    return timer(400).pipe(
      switchMap(() => this.companyClientService.checkPublicId(value)),
      map((result): ValidationErrors | null =>
        result.valid ? null : { publicId: result.code },
      ),
      // A network hiccup shouldn't trap the user in a permanently-invalid
      // field — the same check runs again authoritatively on the server
      // when they actually save.
      catchError(() => of(null)),
    );
  };

  clientForm = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(1)]],
    publicId: ['', [], [this.publicIdValidator]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.pendingLogo = undefined;
      if (this.mode === 'edit' && this.client) {
        this.clientForm.patchValue({
          name: this.client.name,
          publicId: this.client.publicId ?? '',
        });
      } else {
        this.clientForm.reset();
      }
    }
    if (changes['loading']) {
      const previous = changes['loading'].previousValue;
      const current = changes['loading'].currentValue;
      if (previous === true && current === false) {
        this.closeDialog();
      }
    }
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onLogoCropped(logo: Blob) {
    this.pendingLogo = logo;
  }

  submit() {
    if (this.clientForm.valid) {
      this.save.emit({
        name: this.clientForm.value.name!.trim(),
        // Always a defined string, even when empty — that's what tells the
        // backend "clear the reference" rather than "leave it untouched".
        publicId: this.clientForm.value.publicId?.trim() ?? '',
        logo: this.pendingLogo,
      });
    }
  }
}
